/**
 * PawUrgent product page picker.
 *
 * Two-stage selection: pick a size, then pick a pack count that exists for that size.
 * Choosing a size also swaps the gallery to that size's photography, so someone looking at
 * the 70cc tub sees the 30cc tub the moment they switch.
 *
 * Savings, per-unit prices and the "best value" tag are all derived from the variant prices
 * in the payload, so they cannot contradict what Shopify charges.
 */
class PuPdp extends HTMLElement {
  connectedCallback() {
    const payload = this.querySelector('[data-pu-data]');
    if (!payload) return;

    try {
      this.data = JSON.parse(payload.textContent);
    } catch (err) {
      console.error('pu-pdp: could not read variant data', err);
      return;
    }

    this.variants = this.data.variants || [];
    if (!this.variants.length) return;

    this.labels = this.data.labels || {};
    this.el = {
      stage: this.querySelector('[data-pu-stage]'),
      thumbs: this.querySelector('[data-pu-thumbs]'),
      sizes: this.querySelector('[data-pu-sizes]'),
      packs: this.querySelector('[data-pu-packs]'),
      sizeEcho: this.querySelector('[data-pu-size-echo]'),
      packEcho: this.querySelector('[data-pu-pack-echo]'),
      variantId: this.querySelector('[data-pu-variant-id]'),
      addPrice: this.querySelector('[data-pu-add-price]'),
      addLabel: this.querySelector('[data-pu-add-label]'),
      add: this.querySelector('[data-pu-add]'),
      sticky: this.querySelector('[data-pu-sticky]'),
      stickyAdd: this.querySelector('[data-pu-sticky-add]'),
      stickyLabel: this.querySelector('[data-pu-sticky-label]'),
      stickyPrice: this.querySelector('[data-pu-sticky-price]'),
      stickyMeta: this.querySelector('[data-pu-sticky-meta]'),
      headlinePrice: this.querySelector('[data-pu-headline-price]'),
      headlineWas: this.querySelector('[data-pu-headline-was]'),
      stock: this.querySelector('[data-pu-stock]'),
      stockText: this.querySelector('[data-pu-stock-text]'),
      deadline: this.querySelector('[data-pu-deadline]'),
      deadlineClock: this.querySelector('[data-pu-deadline-clock]')
    };

    // Currency symbol taken from a rendered price so formatting matches the shop.
    const sample = this.variants.find((v) => v.priceText) || {};
    this.symbol = (sample.priceText || '').replace(/[\d.,\s]/g, '') || '';

    // Media that belong to one specific size, so the rest can stay visible for every size.
    this.sizeMedia = new Map();
    this.variants.forEach((v) => {
      if (v.mediaId && v.size) this.sizeMedia.set(v.mediaId, v.size);
    });

    /* The page is rendered on the cheapest variant, so a ?variant= link has to be read
       back here and win: an ad or a shared link points at one specific pack and must land
       on it. Without a parameter this falls through to what Liquid already rendered, so
       nothing repaints and there is no flash of the wrong price. */
    const pinned = Number(new URLSearchParams(window.location.search).get('variant'));
    const start =
      (pinned && this.variants.find((v) => v.id === pinned)) ||
      this.variants.find((v) => v.id === this.data.currentId) ||
      this.variants[0];
    this.size = start.size;
    this.pack = start.pack;

    this.bindTabs();
    this.bindGallery();
    this.bindSizes();
    this.bindSticky();
    this.bindPitch();
    this.bindJumps();
    this.bindGalleryFilm();
    this.bindDeadline();
    this.bindStageFit();
    this.render();
  }

  /* ------------------------------------------------------------------ helpers */

  money(pence) {
    return this.symbol + (pence / 100).toFixed(2);
  }

  /**
   * Some products carry marketing text inside the option value itself, e.g.
   * "2 Pack - Save GBP0.90". That would sit next to the badge this page works out for
   * itself, so only the part before the dash is shown. The variant's real name is
   * untouched: orders and receipts still show what Shopify has.
   */
  packLabel(label) {
    return String(label || '').split(/\s[—–·•|-]\s/)[0].trim() || label;
  }

  /** "2 Pack" -> 2. Anything without a number counts as one unit. */
  packCount(label) {
    const found = String(label || '').match(/\d+/);
    return found ? parseInt(found[0], 10) : 1;
  }

  forSize(size) {
    return this.variants.filter((v) => v.size === size);
  }

  current() {
    return (
      this.variants.find((v) => v.size === this.size && v.pack === this.pack) ||
      this.forSize(this.size)[0] ||
      this.variants[0]
    );
  }

  /* -------------------------------------------------------------------- tabs */

  bindTabs() {
    const tabs = [...this.querySelectorAll('[data-pu-tab]')];
    if (!tabs.length) return;

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const slot = tab.dataset.puTab;
        tabs.forEach((t) => {
          const on = t === tab;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        this.querySelectorAll('[data-pu-panel]').forEach((panel) => {
          panel.classList.toggle('is-active', panel.dataset.puPanel === slot);
        });
      });
    });
  }

  /* ----------------------------------------------------------------- gallery */

  bindGallery() {
    if (!this.el.stage) return;

    this.querySelectorAll('[data-pu-thumbs] .pu-pdp__thumb').forEach((thumb) => {
      thumb.addEventListener('click', () => this.showMedia(thumb.dataset.mediaId));
    });

    // Keep the thumbnail strip in step with whatever the stage is scrolled to.
    this.el.stage.addEventListener(
      'scroll',
      () => {
        if (this.scrollRaf) return;
        this.scrollRaf = requestAnimationFrame(() => {
          this.scrollRaf = null;
          const mid = this.el.stage.scrollLeft + this.el.stage.clientWidth / 2;
          let nearest = null;
          let best = Infinity;
          [...this.el.stage.children].forEach((slide) => {
            if (slide.hidden) return;
            const centre = slide.offsetLeft + slide.offsetWidth / 2;
            const gap = Math.abs(centre - mid);
            if (gap < best) {
              best = gap;
              nearest = slide;
            }
          });
          if (nearest) this.markThumb(nearest.dataset.mediaId);
        });
      },
      { passive: true }
    );
  }

  markThumb(mediaId) {
    this.querySelectorAll('[data-pu-thumbs] .pu-pdp__thumb').forEach((t) => {
      t.classList.toggle('is-active', t.dataset.mediaId === String(mediaId));
    });
  }

  showMedia(mediaId, smooth = true) {
    const slide = this.el.stage?.querySelector(`.pu-pdp__slide[data-media-id="${mediaId}"]`);
    if (!slide) return;
    this.el.stage.scrollTo({ left: slide.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
    this.markThumb(mediaId);
    this.fitStage(slide);
  }

  /* The pack shot is 2.25:1 and everything else is square, so one fixed height cannot serve
     both: it either crops the wide one or strands the square one in a band of empty page.
     The stage is given the shape of the slide in view instead, and CSS caps how tall that
     is allowed to get. */
  fitStage(slide) {
    const stage = this.el.stage;
    if (!stage || !slide) return;
    const ratio = parseFloat(slide.dataset.ratio);
    if (Number.isFinite(ratio) && ratio > 0) {
      stage.style.setProperty('--pu-stage-ratio', ratio);
    }
  }

  /* Swiping bypasses showMedia entirely, so the shape has to follow the scroll as well.
     The slide whose centre is nearest the stage's centre is the one being looked at. */
  bindStageFit() {
    const stage = this.el.stage;
    if (!stage) return;

    let queued = false;
    const sync = () => {
      queued = false;
      const mid = stage.scrollLeft + stage.clientWidth / 2;
      let best = null;
      let bestGap = Infinity;
      stage.querySelectorAll('.pu-pdp__slide').forEach((node) => {
        if (node.hidden) return;
        const gap = Math.abs(node.offsetLeft + node.offsetWidth / 2 - mid);
        if (gap < bestGap) { bestGap = gap; best = node; }
      });
      if (best) this.fitStage(best);
    };

    stage.addEventListener('scroll', () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(sync);
    }, { passive: true });

    /* The scroll event is the right signal and it is what fires on a phone, but it is not
       guaranteed: some embedded webviews never deliver it for an inner scroller, and this
       rail is exactly that. A cheap poll backs it up - the common case is one integer
       comparison, and it only does real work when the rail has actually moved. */
    let lastLeft = -1;
    setInterval(() => {
      if (stage.scrollLeft === lastLeft) return;
      lastLeft = stage.scrollLeft;
      sync();
    }, 200);

    sync();
  }

  /** Hide photography that belongs to a different size; leave shared media alone. */
  /* The gallery has to agree with the picker: a slide that says 20ml on the tube must not
     be on screen while 30cc is selected. Each slide carries the size written in its own alt
     text, which is the one label that cannot drift from the picture.

     Photos for a different size are hidden outright. Photos that name no size - the group
     shots, the infographics, the film - stay for every size but move behind the ones that
     do, so the first thing in view is always the size being bought. */
  filterMedia() {
    const order = (container, selector) => {
      if (!container) return;
      const nodes = Array.from(container.querySelectorAll(selector));
      const matching = [];
      const generic = [];

      nodes.forEach((node) => {
        const tag = node.dataset.sizeTag || '';
        if (tag && tag !== this.size) {
          node.hidden = true;
          return;
        }
        node.hidden = false;
        (tag ? matching : generic).push(node);
      });

      // Re-appending in place is a no-op when the order is already right.
      matching.concat(generic).forEach((node) => container.appendChild(node));
    };

    order(this.el.stage, '.pu-pdp__slide');
    order(this.el.thumbs, '.pu-pdp__thumb');

    /* Reordering moves a different picture under the viewport, so the frame has to be
       re-measured against whatever now leads. */
    const lead = this.el.stage?.querySelector('.pu-pdp__slide:not([hidden])');
    if (lead) this.fitStage(lead);

    /* The film belongs second: after the shot that shows the size, before the rest. */
    const lift = (container, selector) => {
      if (!container) return;
      const film = container.querySelector(selector);
      const first = container.querySelector(
        selector.replace('--film', '').replace('--video', '') + ':not([hidden])'
      );
      if (film && first && film !== first && first.nextSibling !== film) {
        first.after(film);
      }
    };
    lift(this.el.stage, '.pu-pdp__slide--film');
    lift(this.el.thumbs, '.pu-pdp__thumb--video');
  }

  /* ------------------------------------------------------------------- sizes */

  bindSizes() {
    this.querySelectorAll('[data-pu-sizes] .pu-pdp__size').forEach((button) => {
      const size = button.dataset.size;
      const any = this.forSize(size).some((v) => v.available);
      button.disabled = !any;
      button.addEventListener('click', () => {
        if (button.disabled || this.size === size) return;
        this.size = size;

        // Keep the same pack count where that size offers it.
        const packs = this.forSize(size);
        if (!packs.some((v) => v.pack === this.pack && v.available)) {
          this.pack = (packs.find((v) => v.available) || packs[0]).pack;
        }
        this.render({ jumpToVariantMedia: true });
      });
    });
  }

  /* ------------------------------------------------------------------- packs */

  renderPacks() {
    if (!this.el.packs) return;

    const rows = this.forSize(this.size).slice().sort((a, b) => this.packCount(a.pack) - this.packCount(b.pack));
    if (!rows.length) return;

    // Unit price comes from the smallest pack, which is what a saving is measured against.
    const base = rows[0];
    const unit = base.price / this.packCount(base.pack);

    let bestValue = null;
    let bestUnit = Infinity;
    rows.forEach((v) => {
      const each = v.price / this.packCount(v.pack);
      if (each < bestUnit - 0.5) {
        bestUnit = each;
        bestValue = v.pack;
      }
    });

    const art = this.querySelector(`.pu-pdp__slide[data-media-id="${this.current().mediaId}"] img`);
    const artSrc = art ? art.currentSrc || art.src : '';

    this.el.packs.innerHTML = '';
    rows.forEach((v) => {
      const count = this.packCount(v.pack);
      const each = v.price / count;
      const saving = Math.round(unit * count - v.price);

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'pu-pdp__pack' + (v.pack === this.pack ? ' is-active' : '');
      button.disabled = !v.available;

      const tags = [];
      if (saving > 0) tags.push(`<span class="pu-pdp__tag pu-pdp__tag--save">${this.labels.save || 'Save'} ${this.money(saving)}</span>`);
      if (v.pack === bestValue && this.labels.bestValue) tags.push(`<span class="pu-pdp__tag pu-pdp__tag--best">${this.labels.bestValue}</span>`);
      // Which pack sells best differs by product, and on a sized product it is a specific
      // size-and-pack combination, so the match is on the variant itself.
      const isPopular = this.data.popularVariantId
        ? v.id === this.data.popularVariantId
        : Boolean(this.labels.popularPack) && v.pack === this.labels.popularPack;
      if (isPopular && this.labels.popular) {
        tags.push(`<span class="pu-pdp__tag pu-pdp__tag--popular">${this.labels.popular}</span>`);
      }

      // Repeat the pack shot once per unit so the row reads as a multipack without
      // pretending to be a photograph of one.
      const stack = artSrc
        ? Array.from({ length: Math.min(count, 3) }, (_, i) => `<img src="${artSrc}" alt="" style="left:${i * 9}px;z-index:${3 - i}">`).join('')
        : '';

      button.innerHTML = `
        <span class="pu-pdp__radio" aria-hidden="true"></span>
        <span class="pu-pdp__pack-art" aria-hidden="true">${stack}</span>
        <span>
          <span class="pu-pdp__pack-name">${this.packLabel(v.pack)}</span>
          <span class="pu-pdp__pack-unit">${this.money(each)} ${this.labels.each || 'each'}</span>
        </span>
        <span class="pu-pdp__pack-right">
          <span class="pu-pdp__pack-price">${v.priceText}</span>
          ${tags.join('')}
        </span>`;

      button.addEventListener('click', () => {
        if (button.disabled) return;
        this.pack = v.pack;
        this.render();
      });

      this.el.packs.appendChild(button);
    });
  }

  /* ------------------------------------------------------------------ render */

  /* The bar stands in for the real button and nothing more: up whenever that button is
     off screen, down whenever it is in view, on a phone and on a desktop alike. The old
     always-on-mobile behaviour put both on screen together at the top of the page, which
     is two buy buttons saying the same thing.

     IntersectionObserver first, as asked. It is the right tool, but it is silently inert
     in some embedded webviews - the Facebook in-app browser among them, which is exactly
     where this traffic comes from - so a scroll listener runs alongside it. Both call the
     same comparison, so whichever fires the answer is identical. */
  bindSticky() {
    const bar = this.el.sticky;
    const anchor = this.el.add;
    if (!bar || !anchor) return;

    bar.hidden = false;
    let queued = false;

    /* Shopify pins its cookie banner to the bottom of the window above everything else,
       so the bar is lifted clear of it while it is on screen. The banner is injected and
       removed at runtime, hence watching the body rather than reading it once. */
    const cookieOffset = () => {
      const banner = document.getElementById('shopify-pc__banner');
      if (!banner) return 0;
      const box = banner.getBoundingClientRect();
      const onScreen = box.height > 0 && box.bottom > window.innerHeight - 4;
      return onScreen ? Math.round(box.height) : 0;
    };

    const sync = () => {
      queued = false;
      const box = anchor.getBoundingClientRect();
      const h = window.innerHeight || document.documentElement.clientHeight;
      bar.classList.toggle('is-on', box.bottom < 0 || box.top > h);
      bar.style.setProperty('--pu-cookie-offset', cookieOffset() + 'px');
    };
    const onChange = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(sync);
    };

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(onChange, { threshold: 0 }).observe(anchor);
    }
    document.addEventListener('scroll', onChange, { capture: true, passive: true });
    window.addEventListener('resize', onChange);
    new MutationObserver(onChange).observe(document.body, { childList: true });
    sync();
  }

  /* The gallery film carries no src until someone asks for it, so the page costs nothing
     for a video most visitors never play. First tap attaches the right rendition, loads
     and plays it; after that the native controls take over. 480p on a phone, 720p above
     it - the slide is never wider than a phone screen, so 1080p is only ever waste. */
  bindGalleryFilm() {
    const video = this.querySelector('[data-pu-gallery-film]');
    const play = this.querySelector('[data-pu-film-play]');
    if (!video || !play) return;

    const pick = () => {
      const scored = (video.dataset.puSources || '')
        .split(/\s+/)
        .filter(Boolean)
        .map((url) => {
          const m = url.match(/[.-](\d{3,4})p-/);
          return { url, h: m ? Number(m[1]) : 0 };
        })
        .sort((a, b) => a.h - b.h);
      if (!scored.length) return '';
      const small = window.matchMedia('(max-width: 767px)').matches;
      if (small) return (scored.find((x) => x.h >= 480) || scored[0]).url;
      for (let i = scored.length - 1; i >= 0; i--) if (scored[i].h <= 720) return scored[i].url;
      return scored[0].url;
    };

    play.addEventListener('click', () => {
      if (!video.dataset.puLoaded) {
        const src = pick();
        if (!src) return;
        video.dataset.puLoaded = '1';
        video.src = src;
        video.load();
      }
      play.hidden = true;
      const started = video.play();
      if (started && started.catch) started.catch(() => {});
    });

    // Swiping away from the film should not leave it playing out of sight.
    const stage = this.el.stage;
    if (stage) {
      stage.addEventListener('scroll', () => {
        const box = video.getBoundingClientRect();
        const off = box.right < 0 || box.left > (window.innerWidth || 0);
        if (off && !video.paused) video.pause();
      }, { passive: true });
    }
  }

  /* Two lines on a phone, the rest behind Read more. The button only appears when the
     text is actually taller than the clamp, so a short pitch gets no pointless control. */
  bindPitch() {
    const pitch = this.querySelector('[data-pu-pitch]');
    const more = this.querySelector('[data-pu-pitch-more]');
    if (!pitch || !more) return;

    const phone = window.matchMedia('(max-width: 767px)');
    const label = more.textContent.trim();

    const apply = () => {
      if (!phone.matches) {
        pitch.removeAttribute('data-clamped');
        more.hidden = true;
        return;
      }
      pitch.setAttribute('data-clamped', '');
      // Measured after clamping, so this asks whether there is anything hidden.
      more.hidden = pitch.scrollHeight <= pitch.clientHeight + 2;
    };

    more.addEventListener('click', () => {
      const open = pitch.hasAttribute('data-clamped');
      if (open) {
        pitch.removeAttribute('data-clamped');
        more.textContent = this.labels.readLess || 'Show less';
      } else {
        pitch.setAttribute('data-clamped', '');
        more.textContent = label;
      }
    });

    if (phone.addEventListener) phone.addEventListener('change', apply);
    window.addEventListener('resize', apply);
    apply();
  }

  /* The rating line and "Read all reviews" scroll to the reviews section, and the offer
     link opens the popup that already exists rather than a second copy of the offer. */
  bindJumps() {
    this.addEventListener('click', (event) => {
      const jump = event.target.closest('[data-pu-jump]');
      if (jump) {
        const target =
          document.querySelector(jump.dataset.puJump) ||
          document.getElementById('pu-reviews') ||
          document.querySelector('[class*="pu-rev__"]');
        if (target) {
          event.preventDefault();
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        return;
      }

      /* The size question is answered once, in the FAQ. This opens that entry and takes
         the visitor to it rather than repeating the answer beside the chips. */
      if (event.target.closest('[data-pu-size-help]')) {
        event.preventDefault();
        const match = [...document.querySelectorAll('details, .pu-faq__item')].find((el) =>
          /which size/i.test(el.textContent || '')
        );
        if (match) {
          if (match.tagName === 'DETAILS') match.open = true;
          const summary = match.querySelector('summary, [aria-expanded]');
          if (summary && summary.getAttribute('aria-expanded') === 'false') summary.click();
          match.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }

      const offer = event.target.closest('[data-pu-offer-open]');
      if (offer) {
        const popup = document.querySelector('[data-pu-offer]');
        if (popup) {
          event.preventDefault();
          popup.hidden = false;
          requestAnimationFrame(() => popup.classList.add('is-open'));
          return;
        }
        // No popup on the page: fall back to the offer box further down.
        const box = document.querySelector('.pu-eo');
        if (box) {
          event.preventDefault();
          box.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    });
  }

  /* The countdown is checked against the browser's clock, never against Liquid's. Shopify
     caches section output, so a server-rendered "11h left" can be served hours after it was
     true; rendering the element hidden and letting the browser decide means a stale copy
     shows nothing rather than something false.

     When it runs out the bar goes away and the page reloads once, so the page stops quoting
     an offer price that the cart would no longer apply. */
  bindDeadline() {
    const bar = this.el.deadline;
    const clock = this.el.deadlineClock;
    if (!bar || !clock) return;

    const endsAt = Date.parse(bar.dataset.puEnds || '');
    if (!Number.isFinite(endsAt)) return;

    let wasLive = false;
    const pad = (n) => String(n).padStart(2, '0');

    const tick = () => {
      const left = endsAt - Date.now();
      if (left <= 0) {
        bar.hidden = true;
        clearInterval(timer);
        if (wasLive) window.location.reload();
        return;
      }
      wasLive = true;
      bar.hidden = false;

      const secs = Math.floor(left / 1000);
      const d = Math.floor(secs / 86400);
      const h = Math.floor((secs % 86400) / 3600);
      const m = Math.floor((secs % 3600) / 60);
      const sec = secs % 60;
      clock.textContent = d > 0
        ? d + 'd ' + pad(h) + 'h ' + pad(m) + 'm'
        : pad(h) + ':' + pad(m) + ':' + pad(sec);
    };

    tick();
    const timer = setInterval(tick, 1000);
  }

  render(options = {}) {
    const variant = this.current();

    this.querySelectorAll('[data-pu-sizes] .pu-pdp__size').forEach((b) => {
      b.classList.toggle('is-active', b.dataset.size === this.size);
    });

    this.filterMedia();
    this.renderPacks();

    // Land on the pack shot for the chosen size. Hiding slides moves everything along, so
    // this has to run after the filter, and without smoothing so scroll-snap cannot drag it
    // back to where it was.
    if (options.jumpToVariantMedia || !this.painted) {
      /* Land on a photo of the size that is selected. The variant's own featured image is
         the best answer when it has one; otherwise the first slide left standing after the
         filter already shows the right size. */
      const stage = this.el.stage;
      const first = stage && stage.querySelector('.pu-pdp__slide:not([hidden])');
      const target =
        (variant.mediaId && stage && stage.querySelector('[data-media-id="' + variant.mediaId + '"]:not([hidden])'))
          ? variant.mediaId
          : (first && first.dataset.mediaId);
      if (target) this.showMedia(target, false);
    }
    this.painted = true;

    if (this.el.sizeEcho) this.el.sizeEcho.textContent = this.size || '';
    if (this.el.packEcho) this.el.packEcho.textContent = this.pack ? this.packLabel(this.pack) : '';
    if (this.el.variantId) this.el.variantId.value = variant.id;
    if (this.el.addPrice) this.el.addPrice.textContent = variant.priceText;
    if (this.el.add) {
      this.el.add.disabled = !variant.available;
      if (this.el.addLabel && !variant.available) this.el.addLabel.textContent = this.labels.soldOut || 'Sold out';
    }

    // Title, pinned bar and buy button all quote the same variant.
    if (this.el.headlinePrice) this.el.headlinePrice.textContent = variant.priceText;
    if (this.el.headlineWas) {
      const was = variant.compareText;
      this.el.headlineWas.hidden = !was;
      if (was) this.el.headlineWas.textContent = was;
    }
    /* Stock is per variant, so it has to move with the picker: a 20cc with eight left and
       a 70cc with forty are different promises. A variant that does not track inventory
       carries null and the line simply stays down. */
    if (this.el.stock) {
      const left = variant.stock;
      const limit = Number(this.el.stock.dataset.puStockThreshold || 0);
      const show = typeof left === 'number' && left > 0 && left <= limit;
      this.el.stock.hidden = !show;
      if (show && this.el.stockText) {
        const tpl = this.el.stock.dataset.puStockTemplate || 'Only [n] left in stock';
        this.el.stockText.textContent = tpl.replace('[n]', left);
      }
    }

    if (this.el.stickyPrice) this.el.stickyPrice.textContent = variant.priceText;
    if (this.el.stickyMeta) {
      this.el.stickyMeta.textContent = [this.size, this.pack ? this.packLabel(this.pack) : '']
        .filter(Boolean)
        .join(' · ');
    }
    if (this.el.stickyAdd) {
      this.el.stickyAdd.disabled = !variant.available;
      if (this.el.stickyLabel && !variant.available) {
        this.el.stickyLabel.textContent = this.labels.soldOut || 'Sold out';
      }
    }

    // Keep the address bar on the selected variant so a shared link opens on it.
    if (window.history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
  }
}

if (!customElements.get('pu-pdp')) customElements.define('pu-pdp', PuPdp);
